import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import lfApi from '@lf/api-client/lfApi';
import { LoadingButton } from '@lf/ui-primitives';
import VerificationCode from '../cards/trial/VerificationCode';
import PlanSelector from '../components/PlanSelector';
import { getUserProfile } from '../api/lfApiUser';
import { getSubscriptionAction } from '../api/lfApiSubscription';

function TrialPage() {
    const [step, setStep] = useState(1); // 1: start, 2: verification, 3: checkout
    const [userProfile, setUserProfile] = useState({ 
        email: '',
        token: ''
    });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    function handleChange(e) {
        const { name, value } = e.target;
        setUserProfile(prevState => ({ ...prevState, [name]: value }));
        setError('');
    }

    function hasEmail() {
        return userProfile.email;
    }

    async function handleStart(event) {
        if (event) event.preventDefault();
        
        if (!hasEmail() || isLoading) {
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(userProfile.email)) {
            setError('Invalid email address');
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            const response = await lfApi.post('/auth/login_cred', {
                type: 'user',
                username: userProfile.email,
            });
            if (response.status === 200 && response.data.success) {
                setStep(2);
            } else {
                setError('Unable to send verification code. Please try again.');
            }
        } catch (error) {
            setError('Something went wrong. Please try again later.');
        }

        setIsLoading(false);
    }

    async function handleVerification() {
        if (!userProfile.token || isLoading) return;

        setIsLoading(true);
        setError('');

        try {
            const response = await lfApi.post('/auth/login_token', {
                type: 'user',
                username: userProfile.email,
                token: userProfile.token
            });

            if (response.status === 200 && response.data.success) {
                const profile = await getUserProfile();
                // Learnforest owns the parent's free trial (granted at signup,
                // no card), so a brand-new parent already has access — send them
                // straight into the app, no checkout step. Only a parent who
                // actually owes payment (expired trial with children, or a
                // canceled subscription) falls through to the Subscribe step.
                const action = await getSubscriptionAction(false);
                if (profile?.data?.user_type === 'premium' || !action || action.action === 'none') {
                    window.location.href = '/';
                } else {
                    setStep(3);
                }
            } else {
                setError('Invalid code, please try again.');
            }
        } catch (error) {
            setError('An error occurred. Please try again later.');
        }

        setIsLoading(false);
    }

    async function handleResendCode() {
        setIsLoading(true);
        setError('');

        try {
            const response = await lfApi.post('/auth/login_cred', {
                type: 'user',
                username: userProfile.email
            });

            if (response.status === 200 && response.data.success) {
                setUserProfile(prevState => ({ ...prevState, token: '' }));
            } else {
                setError('Failed to resend code. Please try again.');
            }
        } catch (error) {
            setError('An error occurred. Please try again later.');
        }

        setIsLoading(false);
    }

    return (
        <div className="flex mx-auto max-w-screen-lg my-8">
            <div className="inline-flex flex-col items-center border border-gray-300 bg-white rounded-2xl mx-auto py-6 px-4 sm:px-20">
                <div className="text-sm text-gray-500 mb-6">Welcome to Learnforest</div>

                {step === 1 && (
                    <>
                        <div className="flex flex-col gap-1 mb-6">
                            <div className="text-lg">Enter your email to start your parent account</div>
                            <div className="text-xs">Already have an account? <Link to="/"><span className="text-blue-500 underline">Login</span></Link></div>
                        </div>

                        <form onSubmit={handleStart} className="flex flex-col w-72 sm:w-96">
                            <div>
                                <input
                                    type="email"
                                    className="border border-gray-300 rounded p-2 my-1 w-full"
                                    name="email"
                                    value={userProfile.email}
                                    placeholder="Email"
                                    onChange={handleChange} />
                            </div>
                            {error && <div className="text-red-500 text-sm">{error}</div>}

                            <div className="flex justify-center w-full">
                                <LoadingButton
                                    type="submit"
                                    className={`${hasEmail() ? "bg-blue-500" : "bg-gray-400"} text-white py-2 px-4 rounded my-2`}
                                    disabled={!hasEmail() || isLoading}
                                    text="Continue"
                                    isLoading={isLoading}
                                    onClick={handleStart} />
                            </div>
                        </form>
                    </>
                )}

                {step === 2 && (
                    <VerificationCode 
                        email={userProfile.email}
                        token={userProfile.token}
                        onTokenChange={(value) => setUserProfile(prev => ({ ...prev, token: value }))}
                        onResend={handleResendCode}
                        isLoading={isLoading}
                        error={error}
                        onSubmit={handleVerification}
                    />
                )}

                {step === 3 && (
                    <div className="w-full max-w-sm">
                        <PlanSelector context="subscribe" surface="trial" />
                        <div className="text-xs text-gray-400 text-center mt-4">
                            Already have an account?{' '}
                            <Link to="/"><span className="text-blue-500 underline">Login</span></Link>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default TrialPage; 
